import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QuestModule } from "./QuestModule";
import { ModuleTask } from "../types";
import {
  ActionButton,
  Button,
  PoweredByBandit,
  ScrollArea,
  Spinner,
  useDisclosure,
  useToast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
  ListItem,
} from "@bandit-network/react-ui-kit";
import { LeaderBoard } from "./leaderboard/LeaderBoard";
import { Participants } from "./participants/Participants";
import { useQuestContext } from "../context/QuestContext";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";
import {
  useConnect,
  useUmi,
  ConnectButton,
  getAddressExplorerUrl,
} from "@bandit-network/react-wallet-kit";
import { useWallet } from "@solana/wallet-adapter-react";
import {
  ReloadIcon,
  ListBulletIcon,
  ExternalLinkIcon,
  CopyIcon,
} from "@radix-ui/react-icons";
import { BlockchainEngineType } from "@bandit-network/react-wallet-kit";
import { useBanditContext } from "../context/BanditQuestContext";
import PeopleIcon from "../components/PeopleIcon";
import LeaderBoardIcon from "../components/LeaderboardIcon";
import { ModuleComplete } from "./ModuleComplete";
import { TaskStatus } from "@bandit-network/sdk-api";
import { QuestComplete } from "./QuestComplete";
import QuestProgress from "../components/QuestProgress";
import { makeFriendlyNumber, truncateAddress } from "@bandit-network/utils";
import {
  GlobeIcon,
  TwitterLogoIcon,
  DiscordLogoIcon,
} from "@radix-ui/react-icons";
import { QuestMode } from "./QuestWidget";
import useUser from "../hooks/useUser";
import { getTarget } from "../utils";
import { isMobile } from "react-device-detect";
import { Address } from "viem";

export interface QuestWidgetViewProps {
  showLeaderBoard: boolean;
  showParticipants: boolean;
  mode: QuestMode;
  onCloseModal?: () => void;
}

const QuestWidgetView: React.FC<QuestWidgetViewProps> = ({
  showLeaderBoard,
  showParticipants,
  mode,
  onCloseModal,
}) => {
  const {
    quest,
    tasksCompleted,
    currentModuleId,
    currentTaskId,
    setCurrentTaskId,
    setCurrentModuleId,
    modulesCompleted,
    isLoading,
    questSDK,
    questId,
    tasksList,
    fetchTasksStatus,
    nonce,
    nonceExpireBy,
    tokensEligibleToClaim,
    isQuestReady,
    isFetchingNonce,
    isClaimed,
    failedToLoadCollection,
  } = useQuestContext();
  const { walletSettings, closeOnSuccess } = useBanditContext();
  const [showModuleCompleted, setShowModuleCompleted] = useState(false);
  const [showQuestCompleted, setShowQuestCompleted] = useState(false);
  const [readMore, setReadMore] = useState(true);

  const [verifying, setVerifying] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const { isWalletConnected, onConnect, signature, blockchainEngine, address } =
    useConnect();
  const { switchChain } = useSwitchChain();
  const { address: evmAddress, chain } = useAccount();
  const { publicKey: solanaAddress } = useWallet();
  const { toast } = useToast();
  const { data: walletClient } = useWalletClient();
  const umi = useUmi();
  const { isOpen: isLeaderboardOpen, onToggle: onToggleLeaderboard } =
    useDisclosure();
  const { isOpen: isParticipantsOpen, onToggle: onToggleParticipants } =
    useDisclosure();
  const { fetchUser } = useUser();

  useEffect(() => {
    if (!!walletSettings?.customSignature) {
      signature?.setSignature(walletSettings?.signature);
    }
  }, [walletSettings, signature]);

  useEffect(() => {
    if (!!currentTaskId) {
      scrollIntoView(currentTaskId);
    }
  }, [currentTaskId]);

  const checkNetwork = useCallback(() => {
    if (blockchainEngine === BlockchainEngineType.SOL) return true;
    return chain?.id === quest?.contract?.chainId;
  }, [chain, quest]);

  const requestNetworkSwitch = useCallback(() => {
    if (switchChain) {
      switchChain({ chainId: quest?.contract?.chainId });
    }
  }, [quest, switchChain]);

  const onBegin = useCallback(() => {
    try {
      const nextTask = tasksList?.find((task) => !task.isCompleted);
      if (!!nextTask) {
        setCurrentModuleId(nextTask.moduleId);
        setCurrentTaskId(nextTask.task.id);
      }
    } catch (error) {}
  }, [quest, currentModuleId, tasksList]);

  const onSign = useCallback(async () => {
    if (!nonce) return;
    signature?.signMessage(nonce, nonceExpireBy);
    await fetchUser();
  }, [nonce, nonceExpireBy, signature, fetchUser]);

  const checkIsModuleComplete = useCallback(
    async (taskStatuses: Array<TaskStatus>) => {
      const completedTasks: Array<string> = taskStatuses.reduce(
        (flat: Array<string>, questModule) => {
          return (flat = [...flat, ...questModule.tasksCompleted]);
        },
        [],
      );

      const availableTasks = (quest?.contestMetaData || []).reduce(
        (flat: Array<string>, questModule) => {
          return (flat = [...flat, ...questModule.tasks.map((t) => t.id)]);
        },
        [],
      );

      if (completedTasks.length === availableTasks.length) {
        setShowQuestCompleted(true);
      } else {
        const currentModule = !!quest?.contestMetaData
          ? quest?.contestMetaData.find((q) => q.id === currentModuleId)
          : null;
        const [moduleJustCompleted] = taskStatuses.filter(
          (ts: TaskStatus) =>
            ts.moduleId === currentModuleId &&
            !ts.isClaimed &&
            ts?.tasksCompleted?.length === currentModule?.tasks?.length,
        );

        if (!!moduleJustCompleted) {
          setShowModuleCompleted(true);
        }
      }
    },
    [modulesCompleted, currentModuleId, quest],
  );

  const onVerify = useCallback(async () => {
    try {
      setVerifying(true);
      const _signature = signature?.data
        ? signature?.data
        : await signature?.signMessage(nonce, nonceExpireBy);
      const taskIndex = tasksList?.findIndex(
        (task: ModuleTask) => task.task.id === currentTaskId,
      );
      const response = await questSDK.verifyContext(
        questId,
        currentModuleId,
        _signature,
        tasksList[taskIndex].task,
        address,
      );

      toast({
        title: "Verified",
        description: "Task completed",
      });

      const taskStatus: any = await fetchTasksStatus();
      checkIsModuleComplete(taskStatus);

      if (!!tasksList[taskIndex + 1]) {
        setCurrentTaskId(tasksList[taskIndex + 1].task.id);
        if (tasksList[taskIndex + 1].moduleId !== currentModuleId) {
          setCurrentModuleId(tasksList[taskIndex + 1].moduleId);
        }
      }
    } catch (error) {
      // TODO: resolve e.json is not a function error
      toast({
        title: "Verification Failed",
        variant: "destructive",
        description: "Something went wrong",
      });
    } finally {
      setVerifying(false);
    }
  }, [
    quest,
    currentModuleId,
    currentTaskId,
    tasksList,
    nonce,
    nonceExpireBy,
    checkIsModuleComplete,
  ]);

  const onContinue = useCallback(() => {
    try {
      const nextTask = tasksList?.find((task) => !task.isCompleted);
      if (!!nextTask) {
        setCurrentModuleId(nextTask.moduleId);
        setCurrentTaskId(nextTask.task.id);
        scrollIntoView(nextTask.task.id);
      }
    } catch (error) {}
  }, [tasksList]);

  const claimEvm = useCallback(async () => {
    try {
      setClaiming(true);

      const _signature = await signature.signMessage(nonce, nonceExpireBy);

      if (quest?.isGasLessMint) {
        const response = await questSDK.claimEvmQuestGaslessToken({
          collectionId: questId,
          chain,
          moduleId: modulesCompleted.map((m) => m.id),
          price: quest?.stats?.price.value,
          questContractAddress: quest?.contract?.contractAddress as Address,
          signature: _signature,
          walletAddress: evmAddress,
          walletClient,
        });
        // TODO: remove
        console.log(response);
      } else {
        const response = await questSDK?.claimEvmQuestToken({
          collectionId: questId,
          chain,
          moduleId: modulesCompleted.map((m) => m.id),
          price: quest?.stats?.price.value,
          questContractAddress: quest?.contract?.contractAddress as Address,
          signature: _signature,
          walletAddress: evmAddress,
          walletClient,
        });
        // TODO: remove
        console.log(response);
      }
      fetchTasksStatus();
      const description = quest?.offChain
        ? "NFTs will be airdropped to your wallet soon"
        : "NFTs are successfully transferred to wallet";
      toast({
        title: "Claim successful",
        description,
      });
      if (closeOnSuccess) {
        onCloseModal && onCloseModal();
      }
    } catch (error) {
      // TODO: resolve e.json is not a function error
      toast({
        title: "Claim failed",
        variant: "destructive",
        description: "Something went wrong",
      });
    } finally {
      setClaiming(false);
    }
  }, [quest, questSDK, currentModuleId, signature, nonce, nonceExpireBy]);

  const claimSolana = useCallback(async () => {
    try {
      setClaiming(true);
      const _signature = await signature.signMessage(nonce, nonceExpireBy);
      const response = await questSDK?.claimSolanaQuestToken({
        collectionId: questId,
        moduleId: modulesCompleted.map((m) => m.id),
        signature: _signature,
        umi,
        walletAddress: solanaAddress.toBase58(),
      });
      console.log(response);
      fetchTasksStatus();
      toast({
        title: "Claim successful",
        description: "NFTs are successfully transferred to wallet",
      });
    } catch (error) {
      // TODO: resolve e.json is not a function error
      toast({
        title: "Claim failed",
        variant: "destructive",
        description: "Something went wrong",
      });
      if (closeOnSuccess) {
        onCloseModal && onCloseModal();
      }
    } finally {
      setClaiming(false);
    }
  }, [
    quest,
    questSDK,
    currentModuleId,
    signature,
    nonce,
    nonceExpireBy,
    solanaAddress,
  ]);

  const totalReward = useMemo(() => {
    return !!quest?.contestMetaData
      ? quest?.contestMetaData.reduce(
          (count, questModule) => count + questModule.rewardData?.count,
          0,
        )
      : 0;
  }, [quest]);

  const isAllModulesComplete = useMemo(() => {
    return quest?.contestMetaData?.length === modulesCompleted.length;
  }, [modulesCompleted, quest]);

  if (isLoading) {
    return (
      <div className="bad-quest__view--loader">
        <Spinner />
      </div>
    );
  }

  if (failedToLoadCollection) {
    return (
      <div className="bad-quest__view--no_collection">
        <p className="bad-quest__view--no_collection_title">
          Uh oh! Something went wrong
        </p>
        <p className="bad-quest__view--no_collection_info">
          Invalid collection id
        </p>
      </div>
    );
  }

  const scrollIntoView = (id: string) => {
    const element = document.getElementById(id);
    element.scrollIntoView({ behavior: "smooth" });
  };

  const copyAddress = (address: string) => {
    if (address) {
      navigator.clipboard.writeText(address);
      toast({
        title: "Address Copied",
      });
    }
  };

  const preventHover = (event: any) => {
    const e = event as Event;
    if (window.innerWidth < 1024) e.preventDefault();
  };

  return (
    <div className="bad-quest__view">
      {
        <div
          className={`bad-quest__view-overlay ${
            showModuleCompleted || showQuestCompleted
              ? "bad-quest__view-overlay--open"
              : ""
          }`}
        >
          {showModuleCompleted && (
            <ModuleComplete
              onContinue={() => {
                setShowModuleCompleted(false);
              }}
            />
          )}
          {showQuestCompleted && (
            <QuestComplete
              onContinue={() => {
                setShowQuestCompleted(false);
                if (blockchainEngine === BlockchainEngineType.SOL) {
                  claimSolana();
                } else {
                  claimEvm();
                }
              }}
            />
          )}
        </div>
      }
      {mode === "default" && (
        <div className="bad-quest__view--connection">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <NavigationMenuTrigger
                        className="bg-gray-50"
                        onPointerMove={preventHover}
                        onPointerLeave={preventHover}
                      >
                        <img
                          src={quest?.profile?.profileImage}
                          alt=""
                          className="bad-quest__details-profile--icon"
                        />
                        <p className="bad-quest__details-name text-left">
                          {quest?.profile?.name}
                        </p>
                      </NavigationMenuTrigger>
                    </TooltipTrigger>
                    {!isMobile && (
                      <TooltipContent>
                        <p>Just Hover</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>

                <NavigationMenuContent
                  onPointerMove={preventHover}
                  onPointerLeave={preventHover}
                >
                  <div className="bad-quest__details">
                    <div className="bad-quest__details-content">
                      <div className="bad-quest__details-cover_wrapper">
                        <img
                          src={quest?.profile?.coverImage}
                          alt=""
                          className="bad-quest__details-cover"
                        />
                        <div className="bad-quest__details-socials--desktop">
                          {quest?.profile?.socialMedia?.website && (
                            <NavigationMenuLink asChild>
                              <a
                                href={quest?.profile?.socialMedia?.website}
                                target="_blank"
                                className="bad-quest__details-socials--desktop_link"
                              >
                                <GlobeIcon className="bad-quest__details-socials--icons" />
                              </a>
                            </NavigationMenuLink>
                          )}
                          {quest?.profile?.socialMedia?.discord && (
                            <NavigationMenuLink asChild>
                              <a
                                href={`https://discord.gg/${quest?.profile?.socialMedia?.discord}`}
                                target="_blank"
                                className="bad-quest__details-socials--desktop_link"
                              >
                                <DiscordLogoIcon className="bad-quest__details-socials--icons" />
                              </a>
                            </NavigationMenuLink>
                          )}
                          {quest?.profile?.socialMedia?.twitter && (
                            <NavigationMenuLink asChild>
                              <a
                                href={`https://twitter.com/${quest?.profile?.socialMedia?.twitter}`}
                                target="_blank"
                                className="bad-quest__details-socials--desktop_link"
                              >
                                <TwitterLogoIcon className="bad-quest__details-socials--icons" />
                              </a>
                            </NavigationMenuLink>
                          )}
                        </div>
                      </div>
                      <div className="bad-quest__details-info">
                        <div className="bad-quest__details-profile_wrapper">
                          <img
                            src={quest?.profile?.profileImage}
                            alt=""
                            className="bad-quest__details-profile"
                          />
                        </div>
                        <div className="bad-quest__details-info_content">
                          <div>
                            <h1 className="bad-quest__details-name">
                              {quest?.profile?.name}
                            </h1>
                            <div className="bad-quest__details-contract">
                              <p className="bad-quest__details-contract--title">
                                Address:
                              </p>
                              <p
                                className="bad-quest__details-contract--address"
                                title={"quest?.contract?.contractAddress"}
                                onPointerMove={preventHover}
                                onPointerLeave={preventHover}
                                onClick={() => {
                                  const chainId = quest?.contract.chainId;
                                  const url = getAddressExplorerUrl(
                                    blockchainEngine ===
                                      BlockchainEngineType.SOL
                                      ? quest?.contract?.candyMachineId
                                      : quest?.contract?.contractAddress,
                                    chainId,
                                  );
                                  window.open(url, getTarget());
                                }}
                              >
                                {truncateAddress(
                                  blockchainEngine === BlockchainEngineType.SOL
                                    ? quest?.contract?.candyMachineId
                                    : quest?.contract?.contractAddress,
                                  4,
                                )}
                              </p>
                              <div
                                className="ml-2 cursor-pointer"
                                onPointerMove={preventHover}
                                onPointerLeave={preventHover}
                                onClick={() =>
                                  copyAddress(
                                    blockchainEngine ===
                                      BlockchainEngineType.SOL
                                      ? quest?.contract?.candyMachineId
                                      : quest?.contract?.contractAddress,
                                  )
                                }
                              >
                                <CopyIcon className="mr-2" />
                              </div>
                            </div>
                          </div>
                          <div className="bad-quest__details-metadata">
                            <div className="bad-quest__details-socials--mobile">
                              {quest?.profile?.socialMedia?.website && (
                                <NavigationMenuLink asChild>
                                  <a
                                    href={quest?.profile?.socialMedia?.website}
                                    target="_blank"
                                    className="bad-quest__details-socials--mobile_link"
                                  >
                                    <GlobeIcon className="bad-quest__details-socials--icons" />
                                  </a>
                                </NavigationMenuLink>
                              )}
                              {quest?.profile?.socialMedia?.discord && (
                                <NavigationMenuLink asChild>
                                  <a
                                    href={`https://discord.gg/${quest?.profile?.socialMedia?.discord}`}
                                    target="_blank"
                                    className="bad-quest__details-socials--mobile_link"
                                  >
                                    <DiscordLogoIcon className="bad-quest__details-socials--icons" />
                                  </a>
                                </NavigationMenuLink>
                              )}
                              {quest?.profile?.socialMedia?.twitter && (
                                <NavigationMenuLink asChild>
                                  <a
                                    href={`https://twitter.com/${quest?.profile?.socialMedia?.twitter}`}
                                    target="_blank"
                                    className="bad-quest__details-socials--mobile_link"
                                  >
                                    <TwitterLogoIcon className="bad-quest__details-socials--icons" />
                                  </a>
                                </NavigationMenuLink>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="bad-quest__details-description--wrapper">
                          <p className={`bad-quest__details-description`}>
                            <span className={` ${readMore && `line-clamp-3`}`}>
                              {/* @ts-ignore */}
                              {quest?.profile?.description}
                            </span>
                            <span
                              className={`font-medium ml-1 ${
                                // @ts-ignore
                                quest?.profile?.description?.length > 160
                                  ? "visible cursor-pointer"
                                  : "invisible"
                              }`}
                              onClick={() => setReadMore(!readMore)}
                            >
                              {readMore ? `read more` : `read less`}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          {!(
            walletSettings?.inheritSolWallets ||
            walletSettings?.inheritEvmWallets
          ) && <ConnectButton showConnectWalletBtn={false} />}
        </div>
      )}
      <div className="bad-quest__view--header">
        <div className="flex items-center justify-between w-full">
          {showLeaderBoard && (
            <div
              className="flex items-center cursor-pointer"
              onClick={onToggleLeaderboard}
            >
              <img
                src="https://assets.bandit.network/assets/leaderboard.svg"
                alt="leaderboard"
                className="bad-quest__leaderboard-image"
              />
              <p className="ml-1 text-xs font-medium">Leaderboard</p>
            </div>
          )}

          {showParticipants && quest?.participantsCount > 0 && (
            <div
              className="flex items-center cursor-pointer ml-auto p-[7px]"
              onClick={onToggleParticipants}
            >
              <img
                src="https://source.boringavatars.com/marble/24/1?colors=1B325F,9CC4E4,E9F2F9,3A89C9,3A89C9"
                className="ml-[-10px]"
                alt="participant"
              />
              <img
                src="https://source.boringavatars.com/marble/24/2?colors=1B325F,9CC4E4,E9F2F9,3A89C9,3A89C9"
                className="ml-[-10px]"
                alt="participant"
              />
              <img
                src="https://source.boringavatars.com/marble/24/1?colors=1B325F,9CC4E4,E9F2F9,3A89C9,3A89C9"
                className="ml-[-10px]"
                alt="participant"
              />
              <p className="ml-1 text-xs font-medium">
                {" "}
                {quest?.participantsCount} participants
              </p>
            </div>
          )}
        </div>
      </div>

      <QuestProgress />

      <ScrollArea id={"scrollRef"} className="bad-quest__view--scroll_area">
        {quest?.contestMetaData?.map((questModule, index) => (
          <QuestModule key={index} data={questModule} />
        ))}
      </ScrollArea>
      <div className="bad-quest__view--footer">
        {mode === "default" ? (
          <p className="bad-quest__view--right_p">
            Reward{" "}
            <span className="bad-quest__view--right_span">{totalReward}</span>{" "}
            NFTs
          </p>
        ) : (
          <div></div>
        )}

        <div className="flex">
          {isWalletConnected && tokensEligibleToClaim > 0 && (
            <Button
              size="sm"
              className="min-w-[120px] mr-2"
              onClick={
                blockchainEngine === BlockchainEngineType.SOL
                  ? claimSolana
                  : claimEvm
              }
              disabled={claiming || !signature?.data || !checkNetwork()}
            >
              <>
                {`Claim ${tokensEligibleToClaim} ${
                  tokensEligibleToClaim > 1 ? "NFTs" : "NFT"
                }`}
                {claiming && (
                  <ReloadIcon className="bad-quest_action_button-reload" />
                )}
              </>
            </Button>
          )}
          <ActionButton
            size="sm"
            className="min-w-[120px]"
            validators={[
              {
                isValid: isWalletConnected,
                fallbackProps: {
                  onClick: () => {
                    document.body.style.pointerEvents = "unset";
                    if (walletSettings?.onConnectClick) {
                      walletSettings?.onConnectClick();
                    } else {
                      onConnect();
                    }
                  },
                  children: "Connect Wallet",
                  disabled: false,
                },
              },
              {
                isValid: !!isQuestReady && !!isFetchingNonce,
                fallbackProps: {
                  children: (
                    <>
                      Loading...
                      <ReloadIcon className="bad-quest_action_button-reload" />
                    </>
                  ),
                  disabled: true,
                },
              },
              {
                isValid:
                  blockchainEngine === BlockchainEngineType.SOL
                    ? true
                    : checkNetwork(),
                fallbackProps: {
                  onClick: requestNetworkSwitch,
                  children: "Switch Network",
                  disabled: false,
                },
              },
              {
                isValid: !!walletSettings?.customSignature
                  ? !!walletSettings?.signature
                  : true,
                fallbackProps: {
                  disabled: true,
                  children: <>Signature required</>,
                },
              },
              {
                isValid: !!signature?.data,
                fallbackProps: {
                  onClick: onSign,
                  disabled: signature?.isSigning,
                  children: (
                    <>
                      Sign
                      {signature?.isSigning && (
                        <ReloadIcon className="bad-quest_action_button-reload" />
                      )}
                    </>
                  ),
                },
              },
              {
                isValid: !(tasksCompleted?.length === 0 && !currentTaskId),
                fallbackProps: {
                  onClick: onBegin,
                  children: "Begin",
                  disabled: false,
                },
              },
              {
                isValid: !(
                  currentTaskId && !tasksCompleted?.includes(currentTaskId)
                ),
                fallbackProps: {
                  onClick: onVerify,
                  disabled: verifying,
                  children: (
                    <>
                      Verify
                      {verifying && (
                        <ReloadIcon className="bad-quest_action_button-reload" />
                      )}
                    </>
                  ),
                },
              },
              {
                isValid: !(tasksList?.length > tasksCompleted.length),
                fallbackProps: {
                  onClick: onContinue,
                  children: "Continue",
                  disabled: !!claiming,
                },
              },
            ]}
            disabled
          >
            Completed
          </ActionButton>
        </div>
      </div>
      <div className="mt-4 relative">
        <a
          className="flex items-center absolute left-2"
          href="https://bandit.freshdesk.com/support/tickets/new"
          target="_blank"
        >
          <p className="text-xs hover:underline">Help</p>
          <ExternalLinkIcon className="ml-1 w-3 h-3" />
        </a>
        <PoweredByBandit />
      </div>

      {isLeaderboardOpen && (
        <LeaderBoard
          isOpen={isLeaderboardOpen}
          onToggle={onToggleLeaderboard}
        />
      )}
      {isParticipantsOpen && (
        <Participants
          isOpen={isParticipantsOpen}
          onToggle={onToggleParticipants}
        />
      )}
    </div>
  );
};

export { QuestWidgetView };
